/**
 * AXEL Performance Card — admin-only view of signal performance.
 *
 * Aggregates over 7d / 30d / all-time and breaks down by asset and setup type.
 * Source of truth = the same axel_signals collection feeding SignalJournalTab,
 * so any number you see here is exactly what users see in their journal — no
 * separate scoring, no marketing fluff.
 */
import React, { useEffect, useState } from "react";

const T = {
  bg: "#07090f", surf: "#0d1117", border: "#1c232f",
  white: "#e6e7ec", muted: "#878a91", gold: "#F5A623",
  green: "#22c55e", red: "#ef4444", blue: "#3b82f6", amber: "#f59e0b",
};
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { id: "7d",  label: "7 DAYS" },
  { id: "30d", label: "30 DAYS" },
  { id: "all", label: "ALL TIME" },
];

const STATUS_COLOR = {
  TP1_HIT: T.green, TP2_HIT: T.green, TP3_HIT: T.green,
  SL_HIT: T.red, EXPIRED: T.muted, INVALIDATED: T.muted,
};

// ── Quant Lab Health widget ─────────────────────────────────────────────
// Pings /api/axel/quant-lab/status every 60s. Three states:
//   🟢 Online + primary engine ready
//   🟡 Online but only baseline engine (production fallback path)
//   🔴 Offline (fetch failed or backend reports online:false)
function QuantLabHealth() {
  const [health, setHealth] = useState({ state: "loading", checkedAt: null });

  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const r = await fetch(`${API}/axel/quant-lab/status`,
                              { credentials: "include" });
        if (cancelled) return;
        if (!r.ok) {
          setHealth({ state: "offline", reason: `HTTP ${r.status}`,
                      checkedAt: new Date().toISOString() });
          return;
        }
        const d = await r.json();
        const state = !d.online ? "offline"
                    : d.primary_ready ? "ready" : "baseline";
        setHealth({ state, engine: d.engine, primary: d.primary_ready,
                    checkedAt: new Date().toISOString() });
      } catch (e) {
        if (!cancelled) {
          setHealth({ state: "offline", reason: "network",
                      checkedAt: new Date().toISOString() });
        }
      }
    };
    probe();
    const id = setInterval(probe, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const cfg = {
    loading:  { dot: T.muted, label: "QUANT LAB", sub: "Checking…" },
    ready:    { dot: T.green, label: "QUANT LAB",
                sub: "Primary engine ready" },
    baseline: { dot: T.amber, label: "QUANT LAB",
                sub: "Baseline engine only (production fallback)" },
    offline:  { dot: T.red,   label: "QUANT LAB",
                sub: `Offline — ${health.reason || "data source unreachable"}` },
  }[health.state];

  return (
    <div data-testid="quant-lab-health"
      style={{ display: "flex", alignItems: "center", gap: 10,
        background: T.surf, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: "8px 12px", marginBottom: 14 }}>
      <span data-testid={`quant-lab-health-dot-${health.state}`}
        style={{ width: 9, height: 9, borderRadius: "50%", background: cfg.dot,
          boxShadow: `0 0 8px ${cfg.dot}88`, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: ".58rem", color: T.muted, letterSpacing: ".14em",
                      textTransform: "uppercase",
                      fontFamily: "Barlow Condensed, sans-serif" }}>
          {cfg.label} HEALTH
        </div>
        <div data-testid="quant-lab-health-sub"
          style={{ fontSize: ".72rem", color: T.white, marginTop: 1,
                   fontFamily: "Barlow, sans-serif" }}>
          {cfg.sub}
        </div>
      </div>
      {health.checkedAt && (
        <div style={{ fontSize: ".58rem", color: T.muted, whiteSpace: "nowrap",
                      fontFamily: "IBM Plex Mono, monospace" }}
          title={health.checkedAt}>
          {relTime(health.checkedAt)} ago
        </div>
      )}
    </div>
  );
}

const fmtPct = (n) => `${(n ?? 0).toFixed ? n.toFixed(1) : n}%`;
const relTime = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

const StatCell = ({ label, value, color = T.white, testId }) => (
  <div style={{ background: T.surf, border: `1px solid ${T.border}`,
                borderRadius: 6, padding: "10px 12px" }}>
    <div style={{ fontSize: ".55rem", color: T.muted, letterSpacing: ".14em",
                  textTransform: "uppercase", fontFamily: "Barlow Condensed, sans-serif",
                  marginBottom: 4 }}>{label}</div>
    <div data-testid={testId} style={{ fontSize: "1.1rem", fontWeight: 800, color,
                  fontFamily: "IBM Plex Mono, monospace" }}>{value}</div>
  </div>
);

// Stacked TP/SL/EXPIRED bar — one slice per outcome, proportional to count.
const OutcomeBar = ({ tp1, tp2, sl, expired, total }) => {
  const safe = (n) => (total > 0 ? (n / total) * 100 : 0);
  // tp1 includes TP2, so "TP1 only" = tp1 - tp2
  const tp1Only = Math.max(0, tp1 - tp2);
  const segs = [
    { w: safe(tp2),     color: T.green, label: "TP2" },
    { w: safe(tp1Only), color: "#16a34a", label: "TP1" },
    { w: safe(sl),      color: T.red,   label: "SL" },
    { w: safe(expired), color: T.muted, label: "EXP" },
  ];
  return (
    <div style={{ display: "flex", height: 8, borderRadius: 2, overflow: "hidden",
                  background: T.bg, border: `1px solid ${T.border}` }}>
      {segs.map((s, i) => s.w > 0 && (
        <div key={i} title={`${s.label} ${s.w.toFixed(0)}%`}
             style={{ width: `${s.w}%`, background: s.color }} />
      ))}
    </div>
  );
};

export default function PerformanceCardTab() {
  const [window, setWindow] = useState("7d");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [confEngine, setConfEngine] = useState("all");

  useEffect(() => {
    let cancelled = false;
    const fetchCard = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API}/axel/signals/performance-card?window=${window}`,
                              { credentials: "include" });
        if (!r.ok) {
          if (cancelled) return;
          setError(r.status === 403 ? "Admin access required." : `Load failed (${r.status})`);
          setCard(null);
        } else {
          const data = await r.json();
          if (!cancelled) setCard(data);
        }
      } catch {
        if (!cancelled) setError("Network error — try refresh.");
      }
      if (!cancelled) setLoading(false);
    };
    fetchCard();
    return () => { cancelled = true; };
  }, [window, reloadKey]);

  const g = card?.global || {};
  const total = g.total ?? 0;
  const best = card?.best_asset;
  const bestSetup = card?.best_setup_type;
  const ratio = card?.tp_sl_ratio;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "8px 18px 40px" }}
         data-testid="performance-card-tab">
      <QuantLabHealth />
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-end", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "1.35rem",
                       fontWeight: 800, color: T.white, margin: 0 }}>
            AXEL Performance Card <span style={{ color: T.gold, fontSize: ".7rem",
              fontWeight: 700, letterSpacing: ".1em", marginLeft: 8 }}>· OWNER / ADMIN</span>
          </h2>
          <div style={{ fontSize: ".62rem", color: T.muted, marginTop: 2,
                        fontFamily: "Barlow, sans-serif" }}>
            Tracked edge from the auto-logged Research Desk signals — same data, no spin.
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {WINDOWS.map(w => (
            <button key={w.id} data-testid={`perf-window-${w.id}`}
              onClick={() => setWindow(w.id)}
              style={{ padding: "5px 11px", borderRadius: 4, cursor: "pointer",
                background: window === w.id ? "#1a1407" : T.bg,
                border: `1px solid ${window === w.id ? T.gold : T.border}`,
                color: window === w.id ? T.gold : T.muted,
                fontFamily: "Barlow Condensed, sans-serif", fontSize: ".7rem",
                fontWeight: 700, letterSpacing: ".08em" }}>
              {w.label}
            </button>
          ))}
          <button data-testid="perf-refresh" onClick={() => setReloadKey(k => k + 1)}
            style={{ padding: "5px 11px", borderRadius: 4, cursor: "pointer",
              background: T.bg, border: `1px solid ${T.border}`, color: T.muted,
              fontSize: ".7rem", fontFamily: "Barlow Condensed, sans-serif" }}>
            ↻
          </button>
        </div>
      </div>

      {/* Loading / error / empty states */}
      {loading && (
        <div data-testid="perf-loading" style={{ color: T.muted, padding: 24,
          textAlign: "center", fontSize: ".72rem" }}>Loading performance…</div>
      )}
      {!loading && error && (
        <div data-testid="perf-error" style={{ color: T.red, padding: 16,
          border: `1px dashed ${T.border}`, borderRadius: 6, textAlign: "center",
          fontSize: ".7rem", fontFamily: "Barlow, sans-serif" }}>{error}</div>
      )}

      {!loading && !error && card && (
        <>
          {/* Headline stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
                        gap: 10, marginBottom: 18 }}>
            <StatCell testId="perf-total"   label="Signals"        value={total} />
            <StatCell testId="perf-closed"  label="Closed"         value={g.closed ?? 0} />
            <StatCell testId="perf-active"  label="Active"         value={g.active ?? 0} color={T.amber} />
            <StatCell testId="perf-tp1"     label="TP Hit %"       value={fmtPct(g.tp1_hit_pct ?? 0)} color={T.green} />
            <StatCell testId="perf-tp2"     label="TP2 Hit %"      value={fmtPct(g.tp2_hit_pct ?? 0)} color={T.green} />
            <StatCell testId="perf-tp3"     label="TP3 Hit %"      value={fmtPct(g.tp3_hit_pct ?? 0)} color={T.green} />
            <StatCell testId="perf-sl"      label="SL Hit %"       value={fmtPct(g.sl_hit_pct ?? 0)}  color={T.red} />
            <StatCell testId="perf-ratio"   label="TP / SL Ratio"
              value={ratio === null || ratio === undefined ? "—" : ratio}
              color={ratio && ratio >= 1 ? T.green : ratio === null || ratio === undefined ? T.muted : T.red} />
            <StatCell testId="perf-expired" label="Expired"        value={g.expired ?? 0} color={T.muted} />
          </div>

          {/* Best asset + best setup highlight cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
                        marginBottom: 18 }}>
            <div data-testid="perf-best-asset" style={{ background: T.surf,
              border: `1px solid ${best ? T.gold : T.border}`, borderRadius: 6,
              padding: "12px 14px" }}>
              <div style={{ fontSize: ".55rem", color: T.muted, letterSpacing: ".14em",
                            textTransform: "uppercase",
                            fontFamily: "Barlow Condensed, sans-serif", marginBottom: 4 }}>
                Top Hit-Rate Asset
              </div>
              {best ? (
                <div>
                  <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "1.1rem",
                                fontWeight: 800, color: T.gold }}>
                    {best.asset} · {fmtPct(best.tp1_hit_pct)} TP1
                  </div>
                  <div style={{ fontSize: ".65rem", color: T.muted, marginTop: 4,
                                fontFamily: "Barlow, sans-serif" }}>
                    {best.tp1_hit}/{best.closed} closed · {best.total} issued
                  </div>
                </div>
              ) : (
                <div style={{ color: T.muted, fontSize: ".72rem",
                              fontFamily: "Barlow, sans-serif" }}>
                  Not enough closed signals yet.
                </div>
              )}
            </div>

            <div data-testid="perf-best-setup" style={{ background: T.surf,
              border: `1px solid ${bestSetup ? T.gold : T.border}`, borderRadius: 6,
              padding: "12px 14px" }}>
              <div style={{ fontSize: ".55rem", color: T.muted, letterSpacing: ".14em",
                            textTransform: "uppercase",
                            fontFamily: "Barlow Condensed, sans-serif", marginBottom: 4 }}>
                Best Setup Type
              </div>
              {bestSetup ? (
                <div>
                  <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "1.1rem",
                                fontWeight: 800, color: T.gold }}>
                    {bestSetup.setup_type} · {fmtPct(bestSetup.tp1_hit_pct)} TP1
                  </div>
                  <div style={{ fontSize: ".65rem", color: T.muted, marginTop: 4,
                                fontFamily: "Barlow, sans-serif" }}>
                    {bestSetup.tp1_hit}/{bestSetup.closed} closed · {bestSetup.total} issued
                  </div>
                </div>
              ) : (
                <div style={{ color: T.muted, fontSize: ".72rem",
                              fontFamily: "Barlow, sans-serif" }}>
                  Setup type not yet labelled in enough signals.
                </div>
              )}
            </div>
          </div>

          {/* MT5 Setup vs Live Trigger comparison — the headline metric */}
          <div data-testid="perf-source-compare" style={{ background: T.surf, border: `1px solid ${T.border}`,
                        borderRadius: 8, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.border}`, background: T.bg }}>
              <div style={{ fontSize: ".6rem", color: T.gold, letterSpacing: ".14em",
                            textTransform: "uppercase", fontFamily: "Barlow Condensed, sans-serif" }}>
                MT5 Setup vs Live Trigger
              </div>
            </div>
            {(!card.per_source || card.per_source.length === 0) ? (
              <div style={{ padding: 18, color: T.muted, fontSize: ".7rem", fontFamily: "Barlow, sans-serif" }}>
                No signals in this window yet.
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "150px 60px 70px 70px 70px 1fr",
                  padding: "6px 14px", fontSize: ".55rem", color: T.muted, letterSpacing: ".08em",
                  textTransform: "uppercase", fontFamily: "Barlow Condensed, sans-serif", borderBottom: `1px solid ${T.border}` }}>
                  <div>Engine</div><div>Issued</div><div>TP1 %</div><div>TP2 %</div><div>SL %</div><div>Outcomes</div>
                </div>
                {card.per_source.map(row => (
                  <div key={row.source} data-testid={`perf-source-${row.source}`}
                    style={{ display: "grid", gridTemplateColumns: "150px 60px 70px 70px 70px 1fr",
                      alignItems: "center", padding: "10px 14px", borderTop: `1px solid ${T.border}`,
                      fontSize: ".72rem", color: T.white, fontFamily: "IBM Plex Mono, monospace" }}>
                    <div style={{ color: row.source === "live_trigger" ? "#F5A623" : "#38bdf8", fontWeight: 700,
                      fontFamily: "Barlow Condensed, sans-serif", letterSpacing: ".04em" }}>
                      {row.source === "live_trigger" ? "⚡ Live Trigger" : "📐 MT5 Setup"}
                    </div>
                    <div style={{ color: T.muted }}>{row.total}</div>
                    <div style={{ color: T.green }}>{fmtPct(row.tp1_hit_pct)}</div>
                    <div style={{ color: T.green }}>{fmtPct(row.tp2_hit_pct)}</div>
                    <div style={{ color: T.red }}>{fmtPct(row.sl_hit_pct)}</div>
                    <OutcomeBar tp1={row.tp1_hit} tp2={row.tp2_hit} sl={row.sl_hit}
                                expired={row.expired} total={row.closed} />
                  </div>
                ))}
                {card.per_source.length >= 2 && (() => {
                  const lt = card.per_source.find(r => r.source === "live_trigger");
                  const mt = card.per_source.find(r => r.source === "mt5_setup");
                  if (!lt || !mt || (lt.closed === 0 && mt.closed === 0)) return null;
                  const winner = (lt.tp1_hit_pct || 0) >= (mt.tp1_hit_pct || 0) ? "Live Trigger" : "MT5 Setup";
                  return (
                    <div data-testid="perf-source-verdict" style={{ padding: "9px 14px", borderTop: `1px solid ${T.border}`,
                      background: T.bg, fontSize: ".68rem", color: T.muted, fontFamily: "Barlow, sans-serif" }}>
                      🏆 Higher TP1 hit-rate: <b style={{ color: T.gold }}>{winner}</b>
                      {" "}({fmtPct(Math.max(lt.tp1_hit_pct || 0, mt.tp1_hit_pct || 0))} vs {fmtPct(Math.min(lt.tp1_hit_pct || 0, mt.tp1_hit_pct || 0))})
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {/* Confidence-bucket breakdown — is AXEL's confidence predictive? */}
          {(() => {
            const buckets = (card.per_confidence && card.per_confidence[confEngine]) || [];
            const populated = buckets.filter(b => b.closed > 0);
            // Edge verdict: compare the highest vs lowest populated tier's TP1 %.
            let verdict = null;
            if (populated.length >= 2) {
              const top = populated[0], bottom = populated[populated.length - 1];
              const predictive = (top.tp1_hit_pct || 0) > (bottom.tp1_hit_pct || 0) + 5;
              verdict = { predictive, top, bottom };
            }
            return (
              <div data-testid="perf-confidence" style={{ background: T.surf, border: `1px solid ${T.border}`,
                            borderRadius: 8, marginBottom: 14, overflow: "hidden" }}>
                <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.border}`, background: T.bg,
                              display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: ".6rem", color: T.gold, letterSpacing: ".14em",
                                textTransform: "uppercase", fontFamily: "Barlow Condensed, sans-serif" }}>
                    By Confidence — is it predictive?
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                    {[["all", "ALL"], ["mt5_setup", "📐 MT5"], ["live_trigger", "⚡ LT"]].map(([id, label]) => (
                      <button key={id} data-testid={`conf-engine-${id}`} onClick={() => setConfEngine(id)}
                        style={{ padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                          background: confEngine === id ? "#1a1407" : T.bg,
                          border: `1px solid ${confEngine === id ? T.gold : T.border}`,
                          color: confEngine === id ? T.gold : T.muted,
                          fontFamily: "Barlow Condensed, sans-serif", fontSize: ".58rem",
                          fontWeight: 700, letterSpacing: ".06em" }}>{label}</button>
                    ))}
                  </div>
                </div>
                {populated.length === 0 ? (
                  <div style={{ padding: 18, color: T.muted, fontSize: ".7rem", fontFamily: "Barlow, sans-serif" }}>
                    No rated &amp; closed signals in this window yet.
                  </div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "90px 60px 70px 70px 70px 1fr",
                      padding: "6px 14px", fontSize: ".55rem", color: T.muted, letterSpacing: ".08em",
                      textTransform: "uppercase", fontFamily: "Barlow Condensed, sans-serif", borderBottom: `1px solid ${T.border}` }}>
                      <div>Confidence</div><div>Closed</div><div>TP1 %</div><div>TP2 %</div><div>SL %</div><div>Outcomes</div>
                    </div>
                    {buckets.map(b => (
                      <div key={b.label} data-testid={`conf-bucket-${b.label}`}
                        style={{ display: "grid", gridTemplateColumns: "90px 60px 70px 70px 70px 1fr",
                          alignItems: "center", padding: "9px 14px", borderTop: `1px solid ${T.border}`,
                          fontSize: ".72rem", color: T.white, fontFamily: "IBM Plex Mono, monospace",
                          opacity: b.closed === 0 ? 0.45 : 1 }}>
                        <div style={{ color: T.gold, fontFamily: "Barlow Condensed, sans-serif", fontWeight: 700 }}>{b.label}</div>
                        <div style={{ color: T.muted }}>{b.closed}</div>
                        <div style={{ color: T.green }}>{b.closed ? fmtPct(b.tp1_hit_pct) : "—"}</div>
                        <div style={{ color: T.green }}>{b.closed ? fmtPct(b.tp2_hit_pct) : "—"}</div>
                        <div style={{ color: T.red }}>{b.closed ? fmtPct(b.sl_hit_pct) : "—"}</div>
                        <OutcomeBar tp1={b.tp1_hit} tp2={b.tp2_hit} sl={b.sl_hit} expired={b.expired} total={b.closed} />
                      </div>
                    ))}
                    {verdict && (
                      <div data-testid="perf-confidence-verdict" style={{ padding: "9px 14px", borderTop: `1px solid ${T.border}`,
                        background: T.bg, fontSize: ".68rem", color: T.muted, fontFamily: "Barlow, sans-serif" }}>
                        {verdict.predictive
                          ? <>✅ <b style={{ color: T.green }}>Confidence is predictive</b> — {verdict.top.label} hits {fmtPct(verdict.top.tp1_hit_pct)} TP1 vs {fmtPct(verdict.bottom.tp1_hit_pct)} for {verdict.bottom.label}.</>
                          : <>⚠️ <b style={{ color: T.amber }}>Not clearly predictive yet</b> — {verdict.top.label} ({fmtPct(verdict.top.tp1_hit_pct)}) isn't beating {verdict.bottom.label} ({fmtPct(verdict.bottom.tp1_hit_pct)}) by much. Needs more closed signals.</>}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Per-asset breakdown */}
          <div style={{ background: T.surf, border: `1px solid ${T.border}`,
                        borderRadius: 8, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.border}`,
                          background: T.bg }}>
              <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em",
                            textTransform: "uppercase",
                            fontFamily: "Barlow Condensed, sans-serif" }}>
                Per-Asset Breakdown
              </div>
            </div>
            {card.per_asset.length === 0 ? (
              <div style={{ padding: 18, color: T.muted, fontSize: ".7rem",
                            fontFamily: "Barlow, sans-serif" }}>
                No closed signals in this window.
              </div>
            ) : card.per_asset.map(row => (
              <div key={row.asset} data-testid={`perf-asset-${row.asset}`}
                style={{ display: "grid",
                  gridTemplateColumns: "110px 60px 60px 60px 60px 1fr",
                  alignItems: "center", padding: "10px 14px",
                  borderTop: `1px solid ${T.border}`,
                  fontSize: ".72rem", color: T.white,
                  fontFamily: "IBM Plex Mono, monospace" }}>
                <div style={{ color: T.gold, fontWeight: 700 }}>{row.asset}</div>
                <div title="Total issued"  style={{ color: T.muted }}>{row.total}</div>
                <div title="TP1 hit %"  style={{ color: T.green }}>{fmtPct(row.tp1_hit_pct)}</div>
                <div title="TP2 hit %"  style={{ color: T.green }}>{fmtPct(row.tp2_hit_pct)}</div>
                <div title="SL hit %"   style={{ color: T.red }}>{fmtPct(row.sl_hit_pct)}</div>
                <OutcomeBar tp1={row.tp1_hit} tp2={row.tp2_hit} sl={row.sl_hit}
                            expired={row.expired} total={row.closed} />
              </div>
            ))}
          </div>

          {/* Per-setup breakdown */}
          <div style={{ background: T.surf, border: `1px solid ${T.border}`,
                        borderRadius: 8, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.border}`,
                          background: T.bg }}>
              <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em",
                            textTransform: "uppercase",
                            fontFamily: "Barlow Condensed, sans-serif" }}>
                Per-Setup-Type Breakdown
              </div>
            </div>
            {card.per_setup_type.length === 0 ? (
              <div style={{ padding: 18, color: T.muted, fontSize: ".7rem",
                            fontFamily: "Barlow, sans-serif" }}>
                Setup labels not yet populated.
              </div>
            ) : card.per_setup_type.map(row => (
              <div key={row.setup_type}
                data-testid={`perf-setup-${row.setup_type.replace(/\s+/g, "-")}`}
                style={{ display: "grid",
                  gridTemplateColumns: "180px 60px 60px 60px 60px 1fr",
                  alignItems: "center", padding: "10px 14px",
                  borderTop: `1px solid ${T.border}`,
                  fontSize: ".72rem", color: T.white,
                  fontFamily: "IBM Plex Mono, monospace" }}>
                <div style={{ color: T.gold, fontWeight: 700 }}>{row.setup_type}</div>
                <div style={{ color: T.muted }}>{row.total}</div>
                <div style={{ color: T.green }}>{fmtPct(row.tp1_hit_pct)}</div>
                <div style={{ color: T.green }}>{fmtPct(row.tp2_hit_pct)}</div>
                <div style={{ color: T.red }}>{fmtPct(row.sl_hit_pct)}</div>
                <OutcomeBar tp1={row.tp1_hit} tp2={row.tp2_hit} sl={row.sl_hit}
                            expired={row.expired} total={row.closed} />
              </div>
            ))}
          </div>

          {/* Last 5 closed */}
          <div style={{ background: T.surf, border: `1px solid ${T.border}`,
                        borderRadius: 8, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.border}`,
                          background: T.bg }}>
              <div style={{ fontSize: ".6rem", color: T.muted, letterSpacing: ".14em",
                            textTransform: "uppercase",
                            fontFamily: "Barlow Condensed, sans-serif" }}>
                Last 5 Closed Signals
              </div>
            </div>
            {card.last_closed.length === 0 ? (
              <div style={{ padding: 18, color: T.muted, fontSize: ".7rem",
                            fontFamily: "Barlow, sans-serif" }}>
                Nothing closed yet in this window.
              </div>
            ) : card.last_closed.map((s, i) => (
              <div key={i} style={{ display: "grid",
                gridTemplateColumns: "70px 80px 60px 1fr 90px",
                alignItems: "center", padding: "9px 14px",
                borderTop: `1px solid ${T.border}`,
                fontSize: ".72rem", color: T.white,
                fontFamily: "IBM Plex Mono, monospace" }}>
                <div style={{ color: T.muted }} title={s.closed_at}>{relTime(s.closed_at)} ago</div>
                <div style={{ color: T.gold, fontWeight: 700 }}>{s.asset}</div>
                <div style={{ color: s.direction === "BUY" ? T.green : T.red,
                              fontWeight: 700 }}>{s.direction}</div>
                <div style={{ color: T.muted, fontFamily: "Barlow, sans-serif",
                              fontSize: ".66rem", overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.setup_type || "—"}
                </div>
                <div>
                  <span style={{
                    background: (STATUS_COLOR[s.status] || T.muted) + "22",
                    color: STATUS_COLOR[s.status] || T.muted,
                    padding: "2px 6px", borderRadius: 3,
                    fontSize: ".55rem",
                    fontFamily: "Barlow Condensed, sans-serif",
                    fontWeight: 800, letterSpacing: ".08em",
                  }}>{s.status?.replace("_", " ")}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
